import { toCsv } from "@/lib/csv";
import { variantLabel } from "@/lib/format";
import { classifyStock, computeAvailable, type StockStatus } from "@/lib/inventory";
import { effectiveCostPrice } from "@/lib/products";

/**
 * Stock quantity and inventory value from canonical catalogue + balance rows.
 *
 * Sources (no parallel cost model):
 * - Quantity: `inventory_balances.on_hand` at variant × location
 * - Unit cost: COALESCE(variants.cost_price, products.cost_price)
 *   via `effectiveCostPrice` — NULL inherits; stored 0 is an intentional A$0.
 * - Selling price: `variants.selling_price`, else `products.selling_price`
 *
 * Null cost is missing, not A$0. Do not treat numeric 0 as inherit.
 */

export type CostDataStatus = "known" | "missing";

export interface CatalogueProduct {
  id: string;
  name: string;
  sku?: string | null;
  cost_price?: unknown;
  selling_price?: unknown;
}

export interface CatalogueVariant {
  id: string;
  product_id: string;
  sku?: string | null;
  cost_price?: unknown;
  selling_price?: unknown;
  option_values?: unknown;
}

export interface InventoryBalanceInput {
  product_id: string;
  variant_id: string | null;
  location_id: string;
  on_hand?: unknown;
  allocated?: unknown;
  available?: unknown;
}

export interface LocationInput {
  id: string;
  name: string;
}

export type StockGrain = "line" | "product" | "variant" | "location";

export const STOCK_REPORT_VIEW_OPTIONS: Array<{ id: StockGrain; label: string }> = [
  { id: "product", label: "By product" },
  { id: "variant", label: "By variant" },
  { id: "location", label: "By location" },
  { id: "line", label: "Variant & location" },
];

export interface StockValuationLine {
  key: string;
  productId: string;
  productName: string;
  variantId: string | null;
  variantSku: string;
  variantLabel: string;
  locationId: string;
  locationName: string;
  onHand: number;
  allocated: number;
  available: number;
  unitCost: number | null;
  costValue: number | null;
  sellingPrice: number | null;
  retailValue: number | null;
  costStatus: CostDataStatus;
  sellingKnown: boolean;
  costSource: "variant" | "product" | "none";
}

export interface StockValuationSummary {
  totalUnitsOnHand: number;
  unitsWithKnownCost: number;
  unitsMissingCost: number;
  inventoryValueAtCost: number;
  costValuationComplete: boolean;
  retailValue: number;
  unitsWithKnownSelling: number;
  unitsMissingSelling: number;
  potentialGrossMargin: number | null;
  unitsInGrossMargin: number;
}

export interface LocationValuation {
  locationId: string;
  locationName: string;
  onHand: number;
  unitsWithKnownCost: number;
  unitsMissingCost: number;
  inventoryValueAtCost: number;
  costValuationComplete: boolean;
  retailValue: number;
}

export interface StockValuationResult {
  lines: StockValuationLine[];
  summary: StockValuationSummary;
  byLocation: LocationValuation[];
}

export function parseCanonicalMoney(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function resolveCatalogueUnitCost(
  variantCost: unknown,
  productCost: unknown,
): { amount: number | null; status: CostDataStatus; source: "variant" | "product" | "none" } {
  const variant = parseCanonicalMoney(variantCost);
  const product = parseCanonicalMoney(productCost);
  const amount = effectiveCostPrice(variant, product);
  if (amount == null) return { amount: null, status: "missing", source: "none" };
  if (variant != null) return { amount, status: "known", source: "variant" };
  return { amount, status: "known", source: "product" };
}

export function resolveCatalogueSellingPrice(variantPrice: unknown, productPrice: unknown): number | null {
  const variant = parseCanonicalMoney(variantPrice);
  if (variant != null) return variant;
  return parseCanonicalMoney(productPrice);
}

export function costDataStatusLabel(status: CostDataStatus): string {
  return status === "known" ? "Known cost" : "Missing cost";
}

export function formatCostAmount(amount: number | null, status: CostDataStatus, moneyFn: (n: number) => string): string {
  if (status === "missing" || amount == null) return "Missing cost";
  return moneyFn(amount);
}

export function formatCostValue(amount: number | null, status: CostDataStatus, moneyFn: (n: number) => string): string {
  if (status === "missing" || amount == null) return "Incomplete valuation";
  return moneyFn(amount);
}

export function formatSellingAmount(amount: number | null, moneyFn: (n: number) => string): string {
  if (amount == null) return "—";
  return moneyFn(amount);
}

export function costValuationHeadline(summary: StockValuationSummary, moneyFn: (n: number) => string): string {
  const value = moneyFn(summary.inventoryValueAtCost);
  if (summary.totalUnitsOnHand === 0) return `${value} · no on-hand stock`;
  if (summary.costValuationComplete) return `${value} · complete`;
  return `${value} · partial — ${summary.unitsWithKnownCost} of ${summary.totalUnitsOnHand} units have known cost`;
}

export interface DashboardStockValueCard {
  /** Known-cost total only. Never fills missing cost with A$0. */
  value: string;
  completeness: "complete" | "partial" | "empty";
  /** Compact second line for the Dashboard snapshot tile. */
  hint: string;
}

/**
 * Compact Dashboard presentation of the same `StockValuationSummary` Reports uses.
 * One contract: do not compute a second stock-value formula on the Dashboard.
 */
export function dashboardStockValueCard(
  summary: StockValuationSummary,
  moneyFn: (n: number) => string,
): DashboardStockValueCard {
  const amount = moneyFn(summary.inventoryValueAtCost);
  if (summary.totalUnitsOnHand === 0) {
    return { value: `${amount} · no stock`, completeness: "empty", hint: "No units on hand" };
  }
  if (summary.costValuationComplete) {
    return {
      value: `${amount} · Complete`,
      completeness: "complete",
      hint: `${summary.unitsWithKnownCost} units on hand`,
    };
  }
  return {
    value: `${amount} · Partial`,
    completeness: "partial",
    hint: `${summary.unitsWithKnownCost} of ${summary.totalUnitsOnHand} units have known cost`,
  };
}

function balanceGrainKey(variantId: string | null, locationId: string, productId: string): string {
  return `${variantId ?? `product:${productId}`}|${locationId}`;
}

export function buildStockValuation(input: {
  products: CatalogueProduct[];
  variants: CatalogueVariant[];
  balances: InventoryBalanceInput[];
  locations: LocationInput[];
  locationId?: string | null;
}): StockValuationResult {
  const productById = new Map(input.products.map((p) => [p.id, p]));
  const variantById = new Map(input.variants.map((v) => [v.id, v]));
  const locationName = new Map(input.locations.map((l) => [l.id, l.name]));

  const merged = new Map<string, {
    productId: string;
    variantId: string | null;
    locationId: string;
    onHand: number;
    allocated: number;
    available: number;
  }>();
  const locationFilter = input.locationId || null;

  for (const b of input.balances) {
    if (locationFilter && b.location_id !== locationFilter) continue;
    const onHand = Number(b.on_hand ?? 0);
    if (!Number.isFinite(onHand)) continue;
    const allocatedRaw = Number(b.allocated ?? 0);
    const allocated = Number.isFinite(allocatedRaw) ? allocatedRaw : 0;
    const availableRaw = b.available == null || b.available === "" ? computeAvailable(onHand, allocated) : Number(b.available);
    const available = Number.isFinite(availableRaw) ? availableRaw : computeAvailable(onHand, allocated);
    const key = balanceGrainKey(b.variant_id, b.location_id, b.product_id);
    const existing = merged.get(key);
    if (existing) {
      // Unique grain is variant × location. Duplicate rows would double-count;
      // keep a single row using the later on_hand rather than summing clones.
      existing.onHand = onHand;
      existing.allocated = allocated;
      existing.available = available;
      continue;
    }
    merged.set(key, {
      productId: b.product_id,
      variantId: b.variant_id,
      locationId: b.location_id,
      onHand,
      allocated,
      available,
    });
  }

  const lines: StockValuationLine[] = [];
  for (const row of merged.values()) {
    if (row.onHand === 0) continue;
    const product = productById.get(row.productId);
    const variant = row.variantId ? variantById.get(row.variantId) : undefined;
    const cost = resolveCatalogueUnitCost(variant?.cost_price, product?.cost_price);
    const selling = resolveCatalogueSellingPrice(variant?.selling_price, product?.selling_price);
    const onHand = row.onHand;
    const costKnown = cost.status === "known" && cost.amount != null;
    const sellingKnown = selling != null;
    lines.push({
      key: `${row.productId}:${row.variantId ?? "none"}:${row.locationId}`,
      productId: row.productId,
      productName: product?.name ?? "—",
      variantId: row.variantId,
      variantSku: variant?.sku || product?.sku || "",
      variantLabel: variantLabel(variant?.option_values) || variant?.sku || product?.sku || "—",
      locationId: row.locationId,
      locationName: locationName.get(row.locationId) ?? "—",
      onHand,
      allocated: row.allocated,
      available: row.available,
      unitCost: costKnown ? cost.amount : null,
      costValue: costKnown ? cost.amount! * onHand : null,
      sellingPrice: sellingKnown ? selling : null,
      retailValue: sellingKnown ? selling! * onHand : null,
      costStatus: cost.status,
      sellingKnown,
      costSource: cost.source,
    });
  }

  lines.sort((a, b) => {
    const byProduct = a.productName.localeCompare(b.productName);
    if (byProduct !== 0) return byProduct;
    const bySku = a.variantSku.localeCompare(b.variantSku);
    if (bySku !== 0) return bySku;
    return a.locationName.localeCompare(b.locationName);
  });

  const summary = summariseLines(lines);
  const byLocation = aggregateByLocation(lines);
  return { lines, summary, byLocation };
}

function summariseLines(lines: StockValuationLine[]): StockValuationSummary {
  let totalUnitsOnHand = 0;
  let unitsWithKnownCost = 0;
  let unitsMissingCost = 0;
  let inventoryValueAtCost = 0;
  let retailValue = 0;
  let unitsWithKnownSelling = 0;
  let unitsMissingSelling = 0;
  let marginRetail = 0;
  let marginCost = 0;
  let unitsInGrossMargin = 0;

  for (const line of lines) {
    totalUnitsOnHand += line.onHand;
    if (line.costStatus === "known" && line.costValue != null) {
      unitsWithKnownCost += line.onHand;
      inventoryValueAtCost += line.costValue;
    } else {
      unitsMissingCost += line.onHand;
    }
    if (line.sellingKnown && line.retailValue != null) {
      unitsWithKnownSelling += line.onHand;
      retailValue += line.retailValue;
    } else {
      unitsMissingSelling += line.onHand;
    }
    if (line.costStatus === "known" && line.costValue != null && line.sellingKnown && line.retailValue != null) {
      marginRetail += line.retailValue;
      marginCost += line.costValue;
      unitsInGrossMargin += line.onHand;
    }
  }

  return {
    totalUnitsOnHand,
    unitsWithKnownCost,
    unitsMissingCost,
    inventoryValueAtCost,
    costValuationComplete: unitsMissingCost === 0,
    retailValue,
    unitsWithKnownSelling,
    unitsMissingSelling,
    potentialGrossMargin: unitsInGrossMargin > 0 ? marginRetail - marginCost : null,
    unitsInGrossMargin,
  };
}

function aggregateByLocation(lines: StockValuationLine[]): LocationValuation[] {
  const map = new Map<string, LocationValuation>();
  for (const line of lines) {
    const row = map.get(line.locationId) ?? {
      locationId: line.locationId,
      locationName: line.locationName,
      onHand: 0,
      unitsWithKnownCost: 0,
      unitsMissingCost: 0,
      inventoryValueAtCost: 0,
      costValuationComplete: true,
      retailValue: 0,
    };
    row.onHand += line.onHand;
    if (line.costStatus === "known" && line.costValue != null) {
      row.unitsWithKnownCost += line.onHand;
      row.inventoryValueAtCost += line.costValue;
    } else {
      row.unitsMissingCost += line.onHand;
    }
    if (line.retailValue != null) row.retailValue += line.retailValue;
    row.costValuationComplete = row.unitsMissingCost === 0;
    map.set(line.locationId, row);
  }
  return [...map.values()].sort((a, b) => a.locationName.localeCompare(b.locationName));
}

export function aggregateStockLines(lines: StockValuationLine[], grain: StockGrain): StockValuationLine[] {
  if (grain === "line") return lines;

  const groups = new Map<string, StockValuationLine[]>();
  for (const line of lines) {
    const key =
      grain === "product" ? line.productId
      : grain === "variant" ? `${line.productId}:${line.variantId ?? "none"}`
      : line.locationId;
    const list = groups.get(key) ?? [];
    list.push(line);
    groups.set(key, list);
  }

  const aggregated: StockValuationLine[] = [];
  for (const [key, group] of groups) {
    const first = group[0];
    const onHand = group.reduce((s, l) => s + l.onHand, 0);
    const anyMissing = group.some((l) => l.costStatus === "missing");
    const knownCosts = group.filter((l) => l.unitCost != null).map((l) => l.unitCost as number);
    const uniqueCosts = [...new Set(knownCosts)];
    const unitCost = anyMissing ? null : uniqueCosts.length === 1 ? uniqueCosts[0] : null;
    const costValue = anyMissing
      ? null
      : group.reduce((s, l) => s + (l.costValue ?? 0), 0);
    const sellingKnown = group.every((l) => l.sellingKnown);
    const uniqueSelling = [...new Set(group.filter((l) => l.sellingPrice != null).map((l) => l.sellingPrice as number))];
    const sellingPrice = sellingKnown && uniqueSelling.length === 1 ? uniqueSelling[0] : sellingKnown ? null : null;
    const retailValue = group.every((l) => l.retailValue != null)
      ? group.reduce((s, l) => s + (l.retailValue ?? 0), 0)
      : null;

    const locationName = grain === "location"
      ? first.locationName
      : group.every((l) => l.locationId === first.locationId)
        ? first.locationName
        : group.length > 1 ? "Multiple locations" : first.locationName;

    const variantSku = grain === "product"
      ? (group.every((l) => l.variantSku === first.variantSku) ? first.variantSku : "")
      : first.variantSku;
    const vLabel = grain === "product"
      ? (group.length === 1 ? first.variantLabel : "All variants")
      : grain === "location"
        ? "All SKUs"
        : first.variantLabel;

    aggregated.push({
      key,
      productId: grain === "location" ? "" : first.productId,
      productName: grain === "location" ? "All products" : first.productName,
      variantId: grain === "variant" ? first.variantId : null,
      variantSku,
      variantLabel: vLabel,
      locationId: grain === "location" ? first.locationId : (group.length === 1 ? first.locationId : ""),
      locationName,
      onHand,
      allocated: group.reduce((s, l) => s + l.allocated, 0),
      available: group.reduce((s, l) => s + l.available, 0),
      unitCost,
      costValue: anyMissing ? null : costValue,
      sellingPrice,
      retailValue,
      costStatus: anyMissing ? "missing" : "known",
      sellingKnown,
      costSource: first.costSource,
    });
  }

  aggregated.sort((a, b) => {
    const byProduct = a.productName.localeCompare(b.productName);
    if (byProduct !== 0) return byProduct;
    return a.locationName.localeCompare(b.locationName);
  });
  return aggregated;
}

export interface StockVariantGroup {
  key: string;
  variantId: string | null;
  sku: string;
  label: string;
  onHand: number;
  allocated: number;
  available: number;
  unitCost: number | null;
  costValue: number | null;
  sellingPrice: number | null;
  retailValue: number | null;
  costStatus: CostDataStatus;
  stockStatus: StockStatus;
  salesQty: number | null;
  locations: StockValuationLine[];
}

export interface StockProductGroup {
  productId: string;
  productName: string;
  onHand: number;
  allocated: number;
  available: number;
  costStatus: CostDataStatus;
  costValue: number | null;
  retailValue: number | null;
  potentialGrossMargin: number | null;
  stockStatus: StockStatus;
  variantCount: number;
  variants: StockVariantGroup[];
}

function uniqueNumber(values: Array<number | null | undefined>): number | null {
  const known = values.filter((v): v is number => v != null);
  const unique = [...new Set(known)];
  return unique.length === 1 ? unique[0] : null;
}

export function groupStockByProduct(
  lines: StockValuationLine[],
  opts?: {
    reorderPointByProduct?: Record<string, number>;
    salesQtyByVariant?: Record<string, number>;
  },
): StockProductGroup[] {
  const byProduct = new Map<string, StockValuationLine[]>();
  for (const line of lines) {
    const list = byProduct.get(line.productId) ?? [];
    list.push(line);
    byProduct.set(line.productId, list);
  }

  const groups: StockProductGroup[] = [];
  for (const [productId, productLines] of byProduct) {
    const byVariant = new Map<string, StockValuationLine[]>();
    for (const line of productLines) {
      const vKey = line.variantId ?? "none";
      const list = byVariant.get(vKey) ?? [];
      list.push(line);
      byVariant.set(vKey, list);
    }

    const reorderPoint = opts?.reorderPointByProduct?.[productId] ?? 0;
    const variants: StockVariantGroup[] = [];
    for (const [vKey, vLines] of byVariant) {
      const first = vLines[0];
      const onHand = vLines.reduce((s, l) => s + l.onHand, 0);
      const allocated = vLines.reduce((s, l) => s + l.allocated, 0);
      const available = vLines.reduce((s, l) => s + l.available, 0);
      const anyMissing = vLines.some((l) => l.costStatus === "missing");
      const costValue = anyMissing ? null : vLines.reduce((s, l) => s + (l.costValue ?? 0), 0);
      const sellingKnown = vLines.every((l) => l.sellingKnown);
      const retailValue = vLines.every((l) => l.retailValue != null)
        ? vLines.reduce((s, l) => s + (l.retailValue ?? 0), 0)
        : null;
      const sales = first.variantId ? opts?.salesQtyByVariant?.[first.variantId] : undefined;
      variants.push({
        key: `${productId}:${vKey}`,
        variantId: first.variantId,
        sku: first.variantSku,
        label: first.variantLabel,
        onHand,
        allocated,
        available,
        unitCost: anyMissing ? null : uniqueNumber(vLines.map((l) => l.unitCost)),
        costValue,
        sellingPrice: sellingKnown ? uniqueNumber(vLines.map((l) => l.sellingPrice)) : null,
        retailValue,
        costStatus: anyMissing ? "missing" : "known",
        stockStatus: classifyStock(onHand, available, reorderPoint),
        salesQty: sales == null ? null : sales,
        locations: [...vLines].sort((a, b) => a.locationName.localeCompare(b.locationName)),
      });
    }
    variants.sort((a, b) => a.sku.localeCompare(b.sku) || a.label.localeCompare(b.label));

    const onHand = variants.reduce((s, v) => s + v.onHand, 0);
    const allocated = variants.reduce((s, v) => s + v.allocated, 0);
    const available = variants.reduce((s, v) => s + v.available, 0);
    const anyMissing = variants.some((v) => v.costStatus === "missing");
    const costValue = anyMissing ? null : variants.reduce((s, v) => s + (v.costValue ?? 0), 0);
    const retailValue = variants.every((v) => v.retailValue != null)
      ? variants.reduce((s, v) => s + (v.retailValue ?? 0), 0)
      : null;
    let marginRetail = 0;
    let marginCost = 0;
    let marginUnits = 0;
    for (const line of productLines) {
      if (line.costStatus === "known" && line.costValue != null && line.sellingKnown && line.retailValue != null) {
        marginRetail += line.retailValue;
        marginCost += line.costValue;
        marginUnits += line.onHand;
      }
    }

    groups.push({
      productId,
      productName: productLines[0].productName,
      onHand,
      allocated,
      available,
      costStatus: anyMissing ? "missing" : "known",
      costValue,
      retailValue,
      potentialGrossMargin: marginUnits > 0 ? marginRetail - marginCost : null,
      stockStatus: classifyStock(onHand, available, reorderPoint),
      variantCount: variants.length,
      variants,
    });
  }

  groups.sort((a, b) => a.productName.localeCompare(b.productName));
  return groups;
}

function costStatusCsv(status: CostDataStatus): string {
  return status === "missing" ? "Missing cost / Incomplete valuation" : "Known cost";
}

export function stockValuationCsv(lines: StockValuationLine[], grain: StockGrain = "line"): string {
  if (grain === "product") {
    const groups = groupStockByProduct(lines);
    return toCsv(
      ["product", "on_hand", "cost_data_status", "cost_value", "retail_value", "potential_gross_margin", "stock_state", "variant_count"],
      groups.map((g) => [
        g.productName,
        g.onHand,
        costStatusCsv(g.costStatus),
        g.costValue == null ? "" : g.costValue,
        g.retailValue == null ? "" : g.retailValue,
        g.potentialGrossMargin == null ? "" : g.potentialGrossMargin,
        g.stockStatus,
        g.variantCount,
      ]),
    );
  }

  const exportLines = grain === "line" ? lines : aggregateStockLines(lines, grain);
  const headers = [
    "product",
    "variant_sku",
    "location",
    "on_hand",
    "unit_cost",
    "cost_value",
    "selling_price",
    "retail_value",
    "cost_data_status",
  ];
  const rows = exportLines.map((l) => [
    l.productName,
    l.variantSku || l.variantLabel,
    l.locationName,
    l.onHand,
    l.unitCost == null ? "" : l.unitCost,
    l.costValue == null ? "" : l.costValue,
    l.sellingPrice == null ? "" : l.sellingPrice,
    l.retailValue == null ? "" : l.retailValue,
    costStatusCsv(l.costStatus),
  ]);
  return toCsv(headers, rows);
}

export function productCostValueMap(lines: StockValuationLine[]): Record<string, { onHand: number; knownCostValue: number; missingUnits: number }> {
  const map: Record<string, { onHand: number; knownCostValue: number; missingUnits: number }> = {};
  for (const line of lines) {
    const row = map[line.productId] ?? { onHand: 0, knownCostValue: 0, missingUnits: 0 };
    row.onHand += line.onHand;
    if (line.costStatus === "known" && line.costValue != null) row.knownCostValue += line.costValue;
    else row.missingUnits += line.onHand;
    map[line.productId] = row;
  }
  return map;
}
